import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, Play, Square, CheckCircle, XCircle, Clock, Ban, RefreshCw, ChevronLeft, ChevronRight, Send } from 'lucide-react';

const SEQ_LABELS = {
  welcome: 'Bienvenue',
  never_published: 'Jamais publie',
  listing_no_sale: 'Sans vente',
};

const STATUS_CONFIG = {
  active: { label: 'Active', icon: Play, color: 'text-green-600 bg-green-50 dark:bg-green-900/30 dark:text-green-400' },
  completed: { label: 'Terminee', icon: CheckCircle, color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400' },
  cancelled: { label: 'Annulee', icon: XCircle, color: 'text-neutral-500 bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-400' },
  stopped: { label: 'Stoppee', icon: Ban, color: 'text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-400' },
};

const CANCEL_REASONS = {
  user_published: 'A publie une annonce',
  listing_inactive: 'Annonce plus active',
  unsubscribed: 'Desinscrit',
  user_inactive: 'Compte inactif',
  admin_stopped: 'Arretee par admin',
  merged_duplicate: 'Doublon fusionne (1 seq/vendeur)',
};

const STEP_SUBJECTS = {
  never_published: ['J+7 Compte vous attend', 'J+15 Acheteurs cherchent', 'J+30 Stock coute', 'J+60 On peut aider'],
  listing_no_sale: ['J+30 Conseils vente', 'J+45 Booster visibilite', 'J+60 Faire le point'],
  welcome: ['J+0 Bienvenue'],
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

export function SequencesTab() {
  const [stats, setStats] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [processing, setProcessing] = useState(false);

  const [unsubscribes, setUnsubscribes] = useState([]);
  const [showUnsubs, setShowUnsubs] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/sequences/stats');
      setStats(data);
    } catch (err) { console.error('Failed to load sequence stats:', err); }
  }, []);

  const loadSequences = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (filterStatus) params.set('status', filterStatus);
      if (filterType) params.set('seq_type', filterType);
      const { data } = await api.get(`/admin/sequences?${params}`);
      setSequences(data.sequences);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { console.error('Failed to load sequences:', err); }
  }, [page, filterStatus, filterType]);

  const loadUnsubs = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/unsubscribes');
      setUnsubscribes(data.unsubscribes);
    } catch (err) { console.error('Failed to load unsubscribes:', err); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadSequences(); }, [loadSequences]);
  useEffect(() => { if (showUnsubs) loadUnsubs(); }, [showUnsubs, loadUnsubs]);

  const stopSequence = async (id) => {
    try {
      await api.post(`/admin/sequences/${id}/stop`);
      toast.success('Sequence arretee');
      loadSequences();
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const triggerProcessing = async () => {
    setProcessing(true);
    try {
      await api.post('/admin/sequences/process');
      toast.success('Traitement termine');
      loadSequences();
      loadStats();
    } catch (err) { toast.error('Erreur de traitement'); }
    finally { setProcessing(false); }
  };

  const sendNow = async (seqId, seqEmail) => {
    if (!window.confirm(`Envoyer l'email maintenant a ${seqEmail} ?`)) return;
    try {
      const { data } = await api.post(`/admin/sequences/${seqId}/send-now`);
      toast.success(data.message);
      loadSequences();
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'envoi"); }
  };

  const restartSequence = async (seqId) => {
    try {
      const { data } = await api.post(`/admin/sequences/${seqId}/restart`);
      toast.success(data.message);
      loadSequences();
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const resubscribe = async (userId, email) => {
    if (!window.confirm(`Reinscrire ${email} aux emails automatiques ?`)) return;
    try {
      await api.delete(`/admin/unsubscribes/${userId}`);
      toast.success(`${email} reinscrit`);
      loadUnsubs();
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const statsByType = stats?.by_type || {};

  return (
    <div className="space-y-6" data-testid="sequences-tab">
      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border dark:border-neutral-700">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Emails envoyes</p>
          <p className="text-2xl font-bold text-neutral-900 dark:text-white">{stats?.total_emails_sent || 0}</p>
        </div>
        <button onClick={() => setShowUnsubs(!showUnsubs)} className="bg-white dark:bg-neutral-800 rounded-xl p-4 border dark:border-neutral-700 text-left hover:border-[#F25C05]/50 transition-colors cursor-pointer">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Desinscrits</p>
          <p className="text-2xl font-bold text-neutral-900 dark:text-white">{stats?.total_unsubscribed || 0}</p>
          <p className="text-[10px] text-[#F25C05]">{showUnsubs ? 'Masquer' : 'Voir la liste'}</p>
        </button>
        {Object.entries(statsByType).map(([type, counts]) => (
          <div key={type} className="bg-white dark:bg-neutral-800 rounded-xl p-4 border dark:border-neutral-700">
            <p className="text-xs text-neutral-500 dark:text-neutral-400">{SEQ_LABELS[type] || type}</p>
            <p className="text-lg font-bold text-neutral-900 dark:text-white">{counts.active} <span className="text-xs font-normal text-neutral-400">actives</span></p>
            <p className="text-xs text-neutral-400">{counts.completed} terminees, {counts.cancelled + counts.stopped} arretees</p>
          </div>
        ))}
      </div>

      {/* Unsubscribed users list */}
      {showUnsubs && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 p-4">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-white mb-3">Utilisateurs desinscrits</h3>
          {unsubscribes.length === 0 ? (
            <p className="text-xs text-neutral-400">Aucun utilisateur desinscrit</p>
          ) : (
            <div className="space-y-2">
              {unsubscribes.map(u => (
                <div key={u.user_id} className="flex items-center justify-between text-xs bg-neutral-50 dark:bg-neutral-700/30 rounded-lg px-3 py-2">
                  <div>
                    <span className="font-medium text-neutral-900 dark:text-white">{u.name || 'Inconnu'}</span>
                    <span className="text-neutral-400 ml-2">{u.email || u.user_id}</span>
                    <span className="text-neutral-400 ml-2">— {fmtDate(u.unsubscribed_at)}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => resubscribe(u.user_id, u.email || u.user_id)} className="text-[10px] text-green-600 border-green-300 hover:bg-green-50" data-testid={`resub-${u.user_id}`}>
                    Reinscrire
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={filterType}
          onChange={e => { setFilterType(e.target.value); setPage(1); }}
          className="text-xs border dark:border-neutral-600 rounded-lg px-3 py-2 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
          data-testid="seq-filter-type"
        >
          <option value="">Tous les types</option>
          <option value="welcome">Bienvenue</option>
          <option value="never_published">Jamais publie</option>
          <option value="listing_no_sale">Sans vente</option>
        </select>
        <select
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
          className="text-xs border dark:border-neutral-600 rounded-lg px-3 py-2 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
          data-testid="seq-filter-status"
        >
          <option value="">Tous les statuts</option>
          <option value="active">Active</option>
          <option value="completed">Terminee</option>
          <option value="cancelled">Annulee</option>
          <option value="stopped">Stoppee</option>
        </select>
        <Button size="sm" variant="outline" onClick={triggerProcessing} disabled={processing} data-testid="seq-process-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${processing ? 'animate-spin' : ''}`} />
          {processing ? 'Traitement...' : 'Traiter maintenant'}
        </Button>
        <span className="text-xs text-neutral-400 ml-auto">{total} sequences</span>
      </div>

      {/* Sequences list */}
      <div className="space-y-2">
        {sequences.map(seq => {
          const statusCfg = STATUS_CONFIG[seq.status] || STATUS_CONFIG.active;
          const StatusIcon = statusCfg.icon;
          const seqDef = SEQ_LABELS[seq.sequence_type] || seq.sequence_type;
          const stepsDef = { welcome: 1, never_published: 4, listing_no_sale: 3 };
          const totalSteps = stepsDef[seq.sequence_type] || 1;

          return (
            <div key={seq.id} className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 p-4" data-testid={`seq-${seq.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${statusCfg.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusCfg.label}
                    </span>
                    <span className="text-[10px] font-medium text-[#F25C05] bg-orange-50 dark:bg-orange-900/30 px-2 py-0.5 rounded-full">{seqDef}</span>
                  </div>
                  <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">{seq.user_name || seq.user_email}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">{seq.user_email}</p>
                  {seq.listing_title && <p className="text-xs text-neutral-400 mt-0.5">Annonce: {seq.listing_title}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Etape {Math.min(seq.current_step, totalSteps)}/{totalSteps}
                  </p>
                  {/* Progress bar */}
                  <div className="w-20 h-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-full mt-1 overflow-hidden">
                    <div className="h-full bg-[#F25C05] rounded-full transition-all" style={{ width: `${(seq.current_step / totalSteps) * 100}%` }} />
                  </div>
                </div>
              </div>

              {/* Steps timeline */}
              <div className="mt-3 space-y-1">
                {(STEP_SUBJECTS[seq.sequence_type] || []).map((label, i) => {
                  const sent = seq.steps_sent?.find(s => s.step === i);
                  const scheduled = seq.scheduled_dates?.[i];
                  const isCurrent = i === seq.current_step && seq.status === 'active';
                  const isPast = i < seq.current_step;
                  const isFuture = i > seq.current_step;
                  return (
                    <div key={label} className={`flex items-center gap-2 text-[10px] px-2 py-1 rounded ${isCurrent ? 'bg-orange-50 dark:bg-orange-900/20 font-medium' : ''}`}>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold shrink-0 ${
                        sent ? 'bg-green-500 text-white' : isCurrent ? 'bg-[#F25C05] text-white' : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400'
                      }`}>{sent ? '\u2713' : i + 1}</span>
                      <span className={`flex-1 ${isPast ? 'text-green-600 dark:text-green-400' : isCurrent ? 'text-[#F25C05]' : 'text-neutral-400'}`}>{label}</span>
                      <span className={`${sent ? 'text-green-600 dark:text-green-400' : 'text-neutral-400'}`}>
                        {sent ? `Envoye ${fmtDate(sent.sent_at)}` : scheduled ? `Prevu ${fmtDate(scheduled)}` : ''}
                      </span>
                      {isCurrent && (
                        <button
                          onClick={() => sendNow(seq.id, seq.user_email)}
                          className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-bold text-white bg-[#F25C05] hover:bg-[#d94f04] px-2 py-0.5 rounded-full transition-colors"
                          data-testid={`seq-send-now-${seq.id}`}
                        >
                          <Send className="w-2.5 h-2.5" /> Envoyer
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Cancel reason */}
              {seq.cancel_reason && (
                <p className="text-[10px] text-neutral-400 mt-1">Raison: {CANCEL_REASONS[seq.cancel_reason] || seq.cancel_reason}</p>
              )}

              {/* Action buttons */}
              {seq.status === 'active' && (
                <div className="mt-3 pt-3 border-t dark:border-neutral-700 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => sendNow(seq.id, seq.user_email)} className="text-xs text-[#F25C05] border-[#F25C05]/30 hover:bg-[#F25C05]/10" data-testid={`seq-send-${seq.id}`}>
                    <Send className="w-3 h-3 mr-1" /> Envoyer maintenant
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => stopSequence(seq.id)} className="text-xs" data-testid={`seq-stop-${seq.id}`}>
                    <Square className="w-3 h-3 mr-1" /> Arreter
                  </Button>
                </div>
              )}
              {(seq.status === 'cancelled' || seq.status === 'stopped') && seq.current_step < (({ welcome: 1, never_published: 4, listing_no_sale: 3 })[seq.sequence_type] || 1) && (
                <div className="mt-3 pt-3 border-t dark:border-neutral-700">
                  <Button size="sm" variant="outline" onClick={() => restartSequence(seq.id)} className="text-xs text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20" data-testid={`seq-restart-${seq.id}`}>
                    <RefreshCw className="w-3 h-3 mr-1" /> Relancer la sequence
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        {sequences.length === 0 && (
          <div className="text-center py-12 text-neutral-400">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Aucune sequence email</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="seq-prev-page">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-neutral-500">{page}/{pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="seq-next-page">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
